import { useEffect, useMemo, useState } from 'react';
import { lookupAdminUsers } from './workflow-api';
import './admin-user-picker.css';

function normalizeEmails(value, multiple) {
  const values = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  return [...new Set(values.map((entry) => String(entry || '').trim().toLowerCase()).filter(Boolean))];
}

export default function AdminUserPicker({
  value,
  onChange,
  getAccessToken,
  multiple = true,
  max = 10,
  disabled = false,
  label = 'Users',
  placeholder = 'Search by name or email',
  help = 'Type at least 2 characters, then select an active Microsoft Entra user.',
}) {
  const selected = useMemo(() => normalizeEmails(value, multiple), [value, multiple]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (disabled || !getAccessToken || term.length < 2) {
      setResults([]);
      setLoading(false);
      setError('');
      setSearched(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await lookupAdminUsers(term, getAccessToken);
        if (!active) return;
        setResults((response?.users || []).filter((user) => !selected.includes(String(user.email || '').toLowerCase())));
        setSearched(true);
      } catch (failure) {
        if (!active) return;
        setResults([]);
        setSearched(true);
        setError(failure?.message || 'User lookup failed.');
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, getAccessToken, disabled, selected.join('|')]);

  function selectUser(user) {
    if (disabled) return;
    const email = String(user?.email || '').trim().toLowerCase();
    if (!email) return;
    if (multiple) {
      if (selected.length >= max || selected.includes(email)) return;
      onChange?.([...selected, email]);
    } else {
      onChange?.(email);
    }
    setQuery('');
    setResults([]);
    setSearched(false);
    setError('');
  }

  function removeUser(email) {
    if (disabled) return;
    if (multiple) onChange?.(selected.filter((entry) => entry !== email));
    else onChange?.('');
  }

  const limitReached = multiple && selected.length >= max;

  return <div className="admin-user-picker">
    <div className="admin-user-picker-label">
      <strong>{label}</strong>
      {multiple && <span className="admin-count">{selected.length}/{max}</span>}
    </div>
    {selected.length > 0 && <div className="admin-user-picker-selected" aria-label={`${label} selected users`}>
      {selected.map((email) => <span className="admin-user-chip" key={email}>
        <span>{email}</span>
        <button type="button" onClick={() => removeUser(email)} disabled={disabled} aria-label={`Remove ${email}`}>×</button>
      </span>)}
    </div>}
    {(!selected.length || multiple) && <div className="admin-user-picker-search">
      <input
        type="search"
        aria-label={label}
        value={query}
        disabled={disabled || limitReached}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={limitReached ? `Maximum ${max} users selected` : placeholder}
        autoComplete="off"
      />
      {loading && <small className="admin-user-picker-status">Searching Microsoft Entra…</small>}
      {!loading && searched && query.trim().length >= 2 && !results.length && !error && <small className="admin-user-picker-status">No active users found.</small>}
      {error && <small className="admin-validation-error">{error}</small>}
      {results.length > 0 && <div className="admin-user-picker-results" role="listbox" aria-label="User lookup results">
        {results.map((user) => <button type="button" disabled={disabled || limitReached} role="option" aria-selected="false" key={user.objectId || user.email} onClick={() => selectUser(user)}>
          <strong>{user.name || user.email}</strong>
          <span>{user.email}</span>
          {user.companyName && <small>{user.companyName}</small>}
        </button>)}
      </div>}
    </div>}
    {help && <small className="admin-input-help">{help}</small>}
  </div>;
}
